import React, { useState, useEffect, useRef } from 'react';
import { t } from '../../i18n';
import { Plus, Trash2, ImagePlus, Camera, X as XIcon, Package, Layers } from 'lucide-react';
import api from '../../services/api';
import { Input, Textarea } from '../../components/common/Input';
import CameraCapture from '../../components/common/CameraCapture';
import IndicateurMarge from '../../components/common/IndicateurMarge';
import InputMontant from '../../components/common/InputMontant';
import { getDeviseCourante } from '../../utils/format';
import { estPerte } from '../../utils/marge';
import toast from 'react-hot-toast';

// ============================================================
// Formulaire produit — modèle « caractéristiques par catégorie » (049)
// ============================================================
// Deux modes, déterminés par la catégorie choisie :
//   • PRODUIT SIMPLE (catégorie sans caractéristique) :
//       nom + prix → un seul article, aucune notion de variante.
//   • PRODUIT À VARIANTES (catégorie avec 1-2 caractéristiques) :
//       chaque ligne = une combinaison de valeurs + prix + (livraison).
//       Le libellé (« 10mm », « 128 Go · Noir ») et le code sont
//       générés automatiquement côté serveur.
// ============================================================

const ligneVide = () => ({
  attributs: {},                 // { "Diamètre": "10mm", ... }
  prix_vente_magasin: 0,
  prix_vente_livraison: 0,
  prix_achat_moyen: 0,           // coût (PAM) — 0 pour une nouvelle variante
});

// Construit l'état initial des lignes à partir d'un produit existant.
function lignesDepuisProduit(produit) {
  if (!produit?.variantes?.length) return [ligneVide()];
  return produit.variantes.map(v => ({
    id: v.id,
    attributs: v.attributs && typeof v.attributs === 'object' ? { ...v.attributs } : {},
    prix_vente_magasin: v.prix_vente_magasin || 0,
    prix_vente_livraison: v.prix_vente_livraison || 0,
    prix_achat_moyen: v.prix_achat_moyen || 0,
    actif: v.actif !== false,
  }));
}

export default function FormProduit({ produit, categories, onSuccess, onCancel }) {
  const [form, setForm] = useState({
    nom: produit?.nom || '',
    categorie_id: produit?.categorie_id || '',
    description: produit?.description || '',
  });
  const [lignes, setLignes] = useState(lignesDepuisProduit(produit));
  // Prix d'un produit simple (1 seule ligne implicite)
  const [prixSimple, setPrixSimple] = useState({
    magasin: produit?.variantes?.[0]?.prix_vente_magasin || 0,
    livraison: produit?.variantes?.[0]?.prix_vente_livraison || 0,
  });
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  // Confirmation explicite si un prix de vente passe sous le coût (PAM).
  const [confirmePerte, setConfirmePerte] = useState(false);
  // Coût (PAM) du produit simple = celui de sa variante unique existante.
  const coutSimple = produit?.variantes?.[0]?.prix_achat_moyen || 0;
  // Seuil de marge faible (orange) + interrupteur du garde-fou (défaut actif).
  const [seuilMarge, setSeuilMarge] = useState(10);
  const [controleActif, setControleActif] = useState(true);
  useEffect(() => {
    api.get('/parametres')
      .then(r => {
        const v = parseFloat(r.data?.marge_seuil_alerte_pct);
        if (Number.isFinite(v) && v >= 0) setSeuilMarge(v);
        setControleActif(r.data?.controle_marge_actif !== 'false');
      })
      .catch(() => {});
  }, []);

  const categorie = categories.find(c => c.id === form.categorie_id);
  const attributs = categorie?.attributs || [];
  const estSimple = !!categorie && attributs.length === 0;

  // IDs des variantes présentes à l'ouverture (pour détecter celles que
  // l'utilisateur retire du formulaire → à désactiver à l'enregistrement).
  const idsOriginaux = (produit?.variantes || []).map(v => v.id).filter(Boolean);

  // ── Photos (inchangé) ──────────────────────────────────────────
  const [photosEnAttente, setPhotosEnAttente] = useState([]);
  const [cameraOuverte, setCameraOuverte] = useState(false);
  const photoInputRef = useRef(null);

  const ajouterPhotos = (files) => {
    const valides = Array.from(files || []).filter(f => f.type.startsWith('image/'));
    if (valides.length === 0) { toast.error('Seules les images sont acceptées'); return; }
    if (photosEnAttente.length + valides.length > 10) { toast.error('Maximum 10 photos par produit'); return; }
    setPhotosEnAttente(prev => [...prev, ...valides.map(file => ({ file, preview: URL.createObjectURL(file) }))]);
  };
  const retirerPhoto = (index) => {
    setPhotosEnAttente(prev => {
      try { URL.revokeObjectURL(prev[index]?.preview); } catch { /* ignore */ }
      return prev.filter((_, i) => i !== index);
    });
  };
  const photosRef = useRef(photosEnAttente);
  useEffect(() => { photosRef.current = photosEnAttente; });
  useEffect(() => () => {
    photosRef.current.forEach(p => { try { URL.revokeObjectURL(p.preview); } catch { /* ignore */ } });
  }, []);

  const uploaderPhotos = async (produitId) => {
    if (photosEnAttente.length === 0) return;
    const fd = new FormData();
    photosEnAttente.forEach(p => fd.append('photos', p.file));
    try {
      await api.post(`/produits/${produitId}/photos`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
    } catch (err) {
      toast.error(`Produit enregistré, mais l'upload des photos a échoué : ${err.response?.data?.message || 'erreur réseau'}`);
    }
  };

  // ── Lignes de variantes ────────────────────────────────────────
  const ajouterLigne = () => setLignes(prev => [...prev, ligneVide()]);
  const supprimerLigne = (i) => {
    if (lignes.length === 1) return toast.error('Au moins une variante');
    setLignes(prev => prev.filter((_, idx) => idx !== i));
  };
  const majAttribut = (i, nomAttr, valeur) => {
    setLignes(prev => prev.map((l, idx) =>
      idx === i ? { ...l, attributs: { ...l.attributs, [nomAttr]: valeur } } : l));
  };
  const majPrix = (i, champ, valeur) => {
    const v = valeur === '' ? 0 : parseInt(valeur) || 0;
    setLignes(prev => prev.map((l, idx) => idx === i ? { ...l, [champ]: v } : l));
  };

  const apercuLibelle = (l) =>
    attributs.map(a => l.attributs[a.nom]).filter(Boolean).join(' · ') || '(à compléter)';

  // ── Garde-fou rentabilité : prix de vente (>0) sous le coût (PAM connu) ──
  const pertes = [];
  if (estSimple) {
    if (estPerte(prixSimple.magasin, coutSimple)) pertes.push('Prix de vente');
    if (estPerte(prixSimple.livraison, coutSimple)) pertes.push('Prix livraison');
  } else {
    lignes.forEach((l) => {
      if (estPerte(l.prix_vente_magasin, l.prix_achat_moyen)) pertes.push(`${apercuLibelle(l)} — vente`);
      if (estPerte(l.prix_vente_livraison, l.prix_achat_moyen)) pertes.push(`${apercuLibelle(l)} — livraison`);
    });
  }
  const aDesPertes = controleActif && pertes.length > 0;

  // ── Validation ─────────────────────────────────────────────────
  const valider = () => {
    const errs = {};
    if (!form.nom.trim()) errs.nom = 'Nom requis';
    if (!form.categorie_id) errs.categorie_id = 'Catégorie requise';

    if (estSimple) {
      if (!prixSimple.magasin || prixSimple.magasin <= 0) errs.prixSimple = 'Prix de vente requis';
    } else if (categorie) {
      lignes.forEach((l, i) => {
        // Chaque caractéristique doit être renseignée
        for (const a of attributs) {
          if (!String(l.attributs[a.nom] || '').trim()) errs[`l${i}_${a.nom}`] = 'requis';
        }
        if (!l.prix_vente_magasin || l.prix_vente_magasin <= 0) errs[`l${i}_prix`] = 'prix requis';
      });
      // Doublons de combinaison
      const vus = new Set();
      lignes.forEach((l, i) => {
        const cle = attributs.map(a => (l.attributs[a.nom] || '').trim().toLowerCase()).join('|');
        if (vus.has(cle)) errs[`l${i}_dup`] = 'combinaison en double';
        vus.add(cle);
      });
    }
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!valider()) return toast.error('Corrigez les champs en rouge');
    if (aDesPertes && !confirmePerte) {
      return toast.error('Un ou plusieurs prix sont inférieurs ou égaux au coût d\'achat : coche la confirmation pour continuer.');
    }
    setLoading(true);
    try {
      // Construire les variantes à envoyer
      const variantesPayload = estSimple
        // Produit simple : on RÉUTILISE l'id de la variante existante (édition)
        // pour la mettre à jour au lieu d'en créer une nouvelle (sinon doublon
        // + perte du stock). En création, id reste undefined → POST.
        ? [{
            id: produit?.variantes?.[0]?.id,
            attributs: {},
            prix_vente_magasin: prixSimple.magasin,
            prix_vente_livraison: prixSimple.livraison,
            actif: true,
          }]
        : lignes.map(l => ({
            id: l.id,
            attributs: l.attributs,
            prix_vente_magasin: l.prix_vente_magasin || 0,
            prix_vente_livraison: l.prix_vente_livraison || 0,
            actif: l.actif !== false,
          }));

      if (produit) {
        await api.put(`/produits/${produit.id}`, { ...form, actif: produit.actif !== false });
        for (const v of variantesPayload) {
          if (v.id) {
            await api.put(`/produits/variantes/${v.id}`, {
              attributs: v.attributs,
              prix_vente_magasin: v.prix_vente_magasin,
              prix_vente_livraison: v.prix_vente_livraison,
              actif: v.actif !== false,
              confirmer_prix_sous_cout: confirmePerte,
            });
          } else {
            await api.post(`/produits/${produit.id}/variantes`, {
              attributs: v.attributs,
              prix_vente_magasin: v.prix_vente_magasin,
              prix_vente_livraison: v.prix_vente_livraison,
            });
          }
        }
        // Variantes retirées du formulaire → désactivées (suppression douce :
        // elles disparaissent du stock et des ventes, l'historique reste).
        const idsConserves = new Set(variantesPayload.filter(v => v.id).map(v => v.id));
        for (const idOrig of idsOriginaux) {
          if (!idsConserves.has(idOrig)) {
            await api.put(`/produits/variantes/${idOrig}`, { actif: false });
          }
        }
        if (photosEnAttente.length > 0) await uploaderPhotos(produit.id);
      } else {
        const { data: cree } = await api.post('/produits', { ...form, variantes: variantesPayload });
        if (photosEnAttente.length > 0 && cree?.id) await uploaderPhotos(cree.id);
      }
      onSuccess();
    } catch (err) {
      toast.error(err.response?.data?.message || t('commun.erreurServeur'));
    } finally {
      setLoading(false);
    }
  };

  const inputCls = 'w-full bg-gray-800 border border-gray-700 text-white rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-yellow-500';

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Infos produit */}
      <div className="space-y-4">
        <h3 className="text-sm font-semibold text-yellow-400 uppercase tracking-wider">Informations générales</h3>

        <div>
          <label className="block text-sm text-gray-300 mb-1">Catégorie *</label>
          <select value={form.categorie_id}
            onChange={e => { setForm({ ...form, categorie_id: e.target.value }); setLignes([ligneVide()]); }}
            className={inputCls}>
            <option value="">Choisir une catégorie…</option>
            {categories.map(c => (
              <option key={c.id} value={c.id}>
                {c.nom}
                {(c.attributs?.length || 0) === 0 ? ' — produit simple' : ` — varie par ${c.attributs.map(a => a.nom).join(', ')}`}
              </option>
            ))}
          </select>
          {errors.categorie_id && <p className="text-xs text-red-400 mt-1">{errors.categorie_id}</p>}
        </div>

        <Input
          label="Nom du produit *"
          value={form.nom}
          onChange={e => setForm({ ...form, nom: e.target.value })}
          error={errors.nom}
          placeholder={estSimple ? 'Ex : Ciment 42.5 — sac 50kg' : 'Ex : Fer à béton TverGsteel'}
        />

        <Textarea
          label="Description"
          value={form.description}
          onChange={e => setForm({ ...form, description: e.target.value })}
          placeholder="Description (optionnel)"
          rows={2}
        />
      </div>

      {/* ── Prix / Variantes selon le type de catégorie ── */}
      {!form.categorie_id ? (
        <p className="text-sm text-gray-500 bg-gray-800/50 border border-gray-700 rounded-lg px-4 py-3">
          Choisis d'abord une catégorie : le formulaire s'adaptera (produit simple ou avec variantes).
        </p>
      ) : estSimple ? (
        // ───── PRODUIT SIMPLE ─────
        <div className="space-y-4">
          <h3 className="text-sm font-semibold text-yellow-400 uppercase tracking-wider flex items-center gap-2">
            <Package size={15} /> Prix & vente
          </h3>
          <div className="bg-gray-800/40 border border-gray-700 rounded-xl p-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-xs text-gray-400 mb-1">Prix de vente ({getDeviseCourante()}) *</label>
              <InputMontant value={prixSimple.magasin}
                onChange={v => setPrixSimple({ ...prixSimple, magasin: v })}
                className={inputCls} placeholder="0" />
              {errors.prixSimple && <p className="text-xs text-red-400 mt-1">{errors.prixSimple}</p>}
              {controleActif && <div><IndicateurMarge prix={prixSimple.magasin} cout={coutSimple} seuilPct={seuilMarge} /></div>}
            </div>
            <div>
              <label className="block text-xs text-gray-400 mb-1">Prix livraison ({getDeviseCourante()})</label>
              <InputMontant value={prixSimple.livraison}
                onChange={v => setPrixSimple({ ...prixSimple, livraison: v })}
                className={inputCls} placeholder="optionnel" />
              {controleActif && prixSimple.livraison > 0 && (
                <div><IndicateurMarge prix={prixSimple.livraison} cout={coutSimple} seuilPct={seuilMarge} /></div>
              )}
            </div>
          </div>
          <p className="text-xs text-gray-500">
            Cette catégorie ne gère pas de variantes : un seul article, un seul prix. Le stock se gère ensuite depuis la page Stock.
          </p>
        </div>
      ) : (
        // ───── PRODUIT À VARIANTES ─────
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-semibold text-yellow-400 uppercase tracking-wider flex items-center gap-2">
              <Layers size={15} /> Variantes ({lignes.length})
            </h3>
            <button type="button" onClick={ajouterLigne}
              className="flex items-center gap-1 text-yellow-400 hover:text-yellow-300 text-sm">
              <Plus size={14} /> Ajouter
            </button>
          </div>
          <p className="text-xs text-gray-500 -mt-1">
            Une ligne par {attributs.map(a => a.nom.toLowerCase()).join(' / ')}. Clique sur une suggestion ou tape ta valeur.
          </p>

          {lignes.map((l, i) => (
            <div key={l.id || `new-${i}`} className="border border-gray-700 rounded-xl p-3 bg-gray-800/40 space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-xs font-medium text-yellow-400/90">
                  {form.nom ? `${form.nom} · ` : ''}{apercuLibelle(l)}
                  {l.id && <span className="text-gray-500 ml-1">(existante)</span>}
                </span>
                <button type="button" onClick={() => supprimerLigne(i)}
                  className="p-1 text-red-400 hover:bg-red-900/30 rounded" title="Retirer">
                  <Trash2 size={13} />
                </button>
              </div>

              {/* Champs des caractéristiques */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {attributs.map(a => (
                  <div key={a.nom}>
                    <label className="block text-xs text-gray-400 mb-0.5">{a.nom}</label>
                    <input
                      list={`sugg-${i}-${a.nom}`}
                      value={l.attributs[a.nom] || ''}
                      onChange={e => majAttribut(i, a.nom, e.target.value)}
                      className={`${inputCls} ${errors[`l${i}_${a.nom}`] ? 'border-red-500' : ''}`}
                      placeholder={a.valeurs_suggerees?.[0] || a.nom}
                    />
                    <datalist id={`sugg-${i}-${a.nom}`}>
                      {(a.valeurs_suggerees || []).map(v => <option key={v} value={v} />)}
                    </datalist>
                  </div>
                ))}
              </div>

              {/* Prix */}
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="block text-xs text-gray-400 mb-0.5">Prix vente ({getDeviseCourante()}) *</label>
                  <InputMontant value={l.prix_vente_magasin}
                    onChange={v => majPrix(i, 'prix_vente_magasin', v)}
                    className={`${inputCls} text-right tabular-nums ${errors[`l${i}_prix`] ? 'border-red-500' : ''}`}
                    placeholder="0" />
                </div>
                <div>
                  <label className="block text-xs text-gray-400 mb-0.5">Prix livraison</label>
                  <InputMontant value={l.prix_vente_livraison}
                    onChange={v => majPrix(i, 'prix_vente_livraison', v)}
                    className={`${inputCls} text-right tabular-nums`}
                    placeholder="optionnel" />
                </div>
              </div>
              {controleActif && (l.prix_vente_magasin > 0 || l.prix_achat_moyen > 0) && (
                <div><IndicateurMarge prix={l.prix_vente_magasin} cout={l.prix_achat_moyen} seuilPct={seuilMarge} /></div>
              )}
              {errors[`l${i}_dup`] && <p className="text-xs text-red-400">⚠️ Cette combinaison existe déjà sur une autre ligne</p>}
            </div>
          ))}
        </div>
      )}

      {/* ── Photos (optionnel) ── */}
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-semibold text-yellow-400 uppercase tracking-wider">
            Photos <span className="text-gray-500 normal-case text-xs">(optionnel — max 10)</span>
          </h3>
          {photosEnAttente.length > 0 && (
            <span className="text-xs text-yellow-400">{photosEnAttente.length} prête{photosEnAttente.length > 1 ? 's' : ''}</span>
          )}
        </div>
        <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-2">
          {photosEnAttente.map((p, i) => (
            <div key={i} className="relative aspect-square bg-gray-800 rounded-lg overflow-hidden group">
              <img src={p.preview} alt={`photo ${i + 1}`} className="w-full h-full object-cover" />
              <button type="button" onClick={() => retirerPhoto(i)}
                className="absolute top-1 right-1 bg-red-600 hover:bg-red-500 text-white p-1 rounded-full opacity-0 group-hover:opacity-100 transition-opacity">
                <XIcon size={12} />
              </button>
              {i === 0 && (
                <div className="absolute bottom-1 left-1 right-1 bg-yellow-500 text-black text-[9px] font-bold text-center py-0.5 rounded">Principale</div>
              )}
            </div>
          ))}
          {photosEnAttente.length < 10 && (
            <button type="button" onClick={() => photoInputRef.current?.click()}
              className="aspect-square bg-gray-800/50 hover:bg-gray-800 border-2 border-dashed border-gray-700 hover:border-yellow-500 rounded-lg flex flex-col items-center justify-center gap-1 text-gray-400 hover:text-yellow-400 transition-colors">
              <ImagePlus size={20} /><span className="text-[10px] font-medium">Fichier</span>
            </button>
          )}
          {photosEnAttente.length < 10 && (
            <button type="button" onClick={() => setCameraOuverte(true)}
              className="aspect-square bg-gray-800/50 hover:bg-gray-800 border-2 border-dashed border-gray-700 hover:border-yellow-500 rounded-lg flex flex-col items-center justify-center gap-1 text-gray-400 hover:text-yellow-400 transition-colors">
              <Camera size={20} /><span className="text-[10px] font-medium">Caméra</span>
            </button>
          )}
        </div>
        <input ref={photoInputRef} type="file" accept="image/*" multiple className="hidden"
          onChange={e => { ajouterPhotos(e.target.files); e.target.value = ''; }} />
      </div>

      {/* Garde-fou : un ou plusieurs prix sous le coût d'achat (PAM) */}
      {aDesPertes && (
        <div className="rounded-xl border border-red-700/50 bg-red-900/25 p-3 space-y-2">
          <p className="text-sm text-red-300 font-semibold">⚠️ Prix ≤ coût d'achat</p>
          <p className="text-xs text-red-200/80">
            {pertes.length} prix inférieur(s) ou égal(aux) au coût d'achat moyen — marge nulle ou négative :
            <br />{pertes.join(' · ')}
          </p>
          <label className="flex items-center gap-2 text-sm text-red-200 cursor-pointer">
            <input
              type="checkbox"
              checked={confirmePerte}
              onChange={e => setConfirmePerte(e.target.checked)}
              className="w-4 h-4 accent-red-500"
            />
            Je confirme volontairement ce(s) prix sous le coût.
          </label>
        </div>
      )}

      {/* Actions */}
      <div className="flex gap-2 pt-2 border-t border-gray-800">
        <button type="button" onClick={onCancel}
          className="flex-1 py-2.5 bg-gray-800 hover:bg-gray-700 text-gray-300 rounded-xl text-sm">
          Annuler
        </button>
        <button type="submit" disabled={loading}
          className="flex-1 py-2.5 bg-yellow-500 hover:bg-yellow-400 disabled:opacity-50 text-black font-bold rounded-xl text-sm">
          {loading ? 'Enregistrement…' : (produit ? 'Enregistrer' : 'Créer le produit')}
        </button>
      </div>

      {cameraOuverte && (
        <CameraCapture
          onCapture={(files) => { ajouterPhotos(files); setCameraOuverte(false); }}
          onClose={() => setCameraOuverte(false)}
        />
      )}
    </form>
  );
}
